// src/hooks/useNotificationListener.tsx
import { useEffect } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { useRouter } from '@tanstack/react-router'
import { supabase } from '@/services/supabase'
import { toast } from 'sonner'
import manifest from '@/lib/manifest/notification.json'
import { resolveRouteFromManifest } from '@/lib/route-manifest'

interface NotificationData {
  id: string
  title: string
  body: string
  isRead: boolean
  action?: {
    entityKey: string
    entityData: Record<string, string>
  }
  createdAt: string
  portalId: string
  orgId: string
}

export function useNotificationListener(
  currentUserId: string | undefined,
  currentPortal: string,
  currentOrg: string | undefined
) {
  const router = useRouter()
  const qc = useQueryClient()

  useEffect(() => {
    if (!currentUserId || !currentPortal || !currentOrg) return

    const channel = supabase
      .channel('notifications-realtime')
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'notifications_data',
          filter: `recipient_uid=eq.${currentUserId}`,
        },
        (payload) => {
          const rawData = payload.new as any
          const data: NotificationData = {
            id: rawData.id,
            title: rawData.title,
            body: rawData.body,
            isRead: rawData.is_read !== undefined ? rawData.is_read : rawData.isRead,
            action: rawData.action,
            createdAt: rawData.created_at || rawData.createdAt,
            portalId: rawData.portal_id || rawData.portalId,
            orgId: rawData.org_id || rawData.orgId,
          }

          // Filter by portal and org
          if (data.portalId !== currentPortal || data.orgId !== currentOrg) return

          // Refresh counters
          qc.refetchQueries({ queryKey: ['notifications-list'] })
          qc.invalidateQueries({ queryKey: ['notifications-count'] })

          const isMention =
            data.body?.toLowerCase().includes('mentioned') ||
            data.title?.toLowerCase().includes('mention') ||
            data.body?.includes('@')

          toast(data.title, {
            description: (
              <span className='block max-w-[240px] truncate text-sm text-muted-foreground'>
                {data.body}
              </span>
            ),
            action: {
              label: 'Open',
              onClick: () => {
                router.navigate({
                  to: resolveRouteFromManifest(manifest, {
                    entityKey: data.action?.entityKey || '',
                    entityData: data.action?.entityData || {},
                  }),
                  params: data.action?.entityData ?? {},
                  state: {
                    openActivity: isMention,
                    activityId: isMention ? data.id : undefined,
                  },
                })
              },
            },
          })
        }
      )
      .subscribe()

    return () => {
      supabase.removeChannel(channel)
    }
  }, [currentUserId, currentPortal, currentOrg, qc, router])
}
